import { useState, forwardRef, useCallback, useEffect } from 'react';
import moment from 'moment';
import DatePicker from 'react-datepicker';

import { formatDate, getGestationAge, parseFather, request } from '../services/utilities';
import { messageService } from '../services/message';
import { notifySuccess, notifyError } from '../services/notify';
import { antenatalAPI } from '../services/constants';
import { useDispatch } from 'react-redux';
import { startBlock, stopBlock } from '../actions/redux-block';
import { toggleSidepanel } from '../actions/sidepanel';

const packageColors = name => {
	const pkg = (name || '').toLowerCase();
	if (pkg.includes('chief')) return 'linear-gradient(135deg, #d4af37 0%, #b8960f 100%)';
	if (pkg.includes('standard')) return 'linear-gradient(135deg, #10b981 0%, #059669 100%)';
	if (pkg.includes('pay') || pkg.includes('go')) return 'linear-gradient(135deg, #ef4444 0%, #dc2626 100%)';
	return 'linear-gradient(135deg, #6366f1 0%, #4f46e5 100%)';
};

const AncBlock = ({ patient, enrollmentId }) => {
	const [loaded, isLoaded] = useState(false);
	const [startDate, setStartDate] = useState(new Date());
	const [item, setItem] = useState(null);

	const dispatch = useDispatch();

	const getEnrollment = useCallback(async () => {
		try {
			const url = `${antenatalAPI}/${enrollmentId}`;
			const rs = await request(url, 'GET', true);
			setItem(rs);

			const lmp = rs.lmp ? new Date(moment(rs.lmp)) : new Date();
			setStartDate(lmp);
			isLoaded(true);
		} catch (error) {
			isLoaded(true);
			notifyError('could not get enrollment');
		}
	}, [enrollmentId]);

	useEffect(() => {
		if (!loaded && enrollmentId) {
			getEnrollment();
		}
	}, [enrollmentId, getEnrollment, loaded]);

	let pregnancy_history = null;
	if (item) {
		try {
			pregnancy_history = JSON.parse(item.pregnancy_history);
		} catch (e) {
			pregnancy_history = item.pregnancy_history;
		}
	}

	const CustomInput = forwardRef(({ value, onClick }, ref) => (
		<button
			className="btn btn-link p-1 ml-2 text-primary"
			onClick={() => {
				if (item.status === 0) {
					onClick();
				}
			}}
			ref={ref}
			title="Edit LMP"
			style={{ textDecoration: 'none', display: 'inline' }}
		>
			<strong className="text-dark">LMP:</strong> {formatDate(item.lmp, 'MMM Do, YYYY')}{' '}
			{item.status === 0 ? <i className="os-icon os-icon-edit-32" /> : <i />}
		</button>
	));

	const onSelectLmpDate = async date => {
		try {
			dispatch(startBlock());
			const url = `patient/antenatal/${enrollmentId}/lmp`;

			const lmp = moment(date).format('YYYY-MM-DD');
			const edd = moment(date).add(40, 'w').format('YYYY-MM-DD');

			const rs = await request(url, 'POST', true, { lmp, edd });
			dispatch(stopBlock());
			if (rs.success) {
				const data = rs.data;
				const enrollment = { ...item, lmp: data.lmp, edd: data.edd };
				const info = { patient, type: 'antenatal', item: enrollment };
				messageService.sendMessage({ type: 'anc', data: enrollment });
				dispatch(toggleSidepanel(true, info));
				setItem(enrollment);
				notifySuccess('LMP saved!');
			} else {
				notifyError('Error saving LMP');
			}
		} catch (error) {
			console.log(error);
			dispatch(stopBlock());
			notifyError('Error saving LMP');
		}
	};

	const isClosed = item?.status === 1;
	const daysToDelivery = item && !isClosed ? moment(item.edd).diff(moment(), 'days') : 0;
	const gestationAge = item && !isClosed ? getGestationAge(item.lmp) : '--';

	return (
		item && (
			<div className="card mb-4 shadow-sm">
				{/* Header with ANC info and prominent package display */}
				<div className="card-header bg-light border-bottom">
					<div className="d-flex justify-content-between align-items-center flex-wrap">
						<div className="d-flex align-items-center mb-2 mb-md-0">
							<span className="badge badge-primary badge-pill px-3 py-2 mr-3 h6 mb-0">{item.serial_code}</span>
							<small className="text-muted">Enrolled: {formatDate(item.createdAt, 'DD MMM, YYYY')}</small>
						</div>

						{/* Prominent Package Display */}
						<div className="d-flex align-items-center">
							<small className="text-muted mr-2">Package:</small>
							<span
								className="badge px-3 py-2 h5 mb-0 text-white"
								style={{
									background: packageColors(item.ancpackage?.name),
									boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
								}}
							>
								{item.ancpackage?.name || 'No Package'}
							</span>
						</div>
					</div>
				</div>

				{/* Patient and Father Info */}
				<div className="card-body bg-light py-2">
					<div className="row">
						<div className="col-md-6 mb-1">
							<strong className="text-dark">Father:</strong>
							<span className="ml-2 text-muted">{parseFather(item.father) || 'Not specified'}</span>
						</div>
						<div className="col-md-6 mb-1">
							<strong className="text-dark">Reason:</strong>
							<span className="badge badge-warning ml-2 px-2 py-1">{item.booking_period}</span>
						</div>
					</div>
				</div>

				{/* Pregnancy Dates */}
				<div className="card-body py-2 border-bottom">
					<div className="row">
						<div className="col-md-6 mb-1">
							<DatePicker
								selected={startDate}
								onChange={date => {
									setStartDate(date);
									onSelectLmpDate(date);
								}}
								customInput={<CustomInput />}
								dateFormat="dd-MMM-yyyy"
								maxDate={new Date()}
							/>
						</div>
						<div className="col-md-6 mb-1">
							<strong className="text-dark">EDD:</strong>
							<span className="ml-2 text-info font-weight-bold">{formatDate(item.edd, 'DD MMM, YYYY')}</span>
						</div>
					</div>
				</div>

				{/* Pregnancy Stats */}
				<div className="card-body">
					<div className="row text-center">
						<div className="col-4 col-md-2 mb-2">
							<div className="h5 mb-0 text-dark">
								{pregnancy_history?.gravida ? pregnancy_history.gravida.replace(/[^0-9]/g, '') : '--'}
							</div>
							<small className="text-muted">Gravida</small>
						</div>
						<div className="col-4 col-md-2 mb-2">
							<div className="h5 mb-0 text-dark">{pregnancy_history?.para || '--'}</div>
							<small className="text-muted">Para</small>
						</div>
						<div className="col-4 col-md-2 mb-2">
							<div className="h5 mb-0 text-success">{pregnancy_history?.alive || '--'}</div>
							<small className="text-muted">Alive</small>
						</div>
						<div className="col-4 col-md-2 mb-2">
							<div className="h5 mb-0 text-warning">{pregnancy_history?.miscarriage || '--'}</div>
							<small className="text-muted">Miscarriages</small>
						</div>
						<div className="col-4 col-md-2 mb-2">
							<div className="h5 mb-0 text-danger">{pregnancy_history?.abortion || '--'}</div>
							<small className="text-muted">Abortions</small>
						</div>
						<div className="col-4 col-md-2 mb-2">
							<div className="h5 mb-0 text-primary font-weight-bold">{gestationAge}</div>
							<small className="text-muted">Gestation Age</small>
						</div>
					</div>

					{/* Days to delivery - prominent display */}
					{!isClosed && (
						<div className="mt-3 pt-3 border-top text-center">
							<div
								className="d-inline-flex align-items-center px-3 py-2 rounded"
								style={{ background: 'linear-gradient(135deg, #fce7f3 0%, #fdf2f8 100%)' }}
							>
								<small className="text-muted mr-2">Days to delivery:</small>
								<span className="h4 mb-0 text-danger font-weight-bold">{daysToDelivery > 0 ? daysToDelivery : 'Overdue'}</span>
								{daysToDelivery > 0 && <small className="text-muted ml-1">days</small>}
							</div>
						</div>
					)}
					{isClosed && (
						<div className="mt-3 pt-3 border-top text-center">
							<span className="badge badge-secondary px-3 py-2">Closed — {formatDate(item.date_closed, 'DD MMM, YYYY')}</span>
						</div>
					)}
				</div>
			</div>
		)
	);
};

export default AncBlock;
